import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { environment } from '@/shared/config/environment';

interface RetryableRequest extends InternalAxiosRequestConfig {
  sessionRefreshAttempted?: boolean;
}

const sessionClient = axios.create({
  baseURL: environment.apiBaseUrl,
  timeout: 15_000,
  withCredentials: true,
  headers: { Accept: 'application/json' },
});

export const httpClient = axios.create({
  baseURL: environment.apiBaseUrl,
  timeout: 20_000,
  withCredentials: true,
  headers: { Accept: 'application/json' },
});

let refreshRequest: Promise<boolean> | undefined;

function getOptionalLockManager(browser: { locks?: LockManager }) {
  return browser.locks;
}

async function refreshOrReuseSession(): Promise<boolean> {
  try {
    // Another browser tab may have refreshed the shared HttpOnly cookies while
    // this tab was waiting for the cross-tab lock. Reuse that session instead
    // of rotating the refresh token a second time.
    const profile = await sessionClient.get<unknown>('/auth/profile');
    if (profile.status >= 200 && profile.status < 300) return true;
  } catch (error) {
    if (!axios.isAxiosError(error) || error.response?.status !== 401) {
      return false;
    }
  }

  try {
    const response = await sessionClient.post<unknown>('/auth/admin/refresh');
    return (
      typeof response.data === 'object' &&
      response.data !== null &&
      'user' in response.data &&
      typeof response.data.user === 'object' &&
      response.data.user !== null
    );
  } catch {
    return false;
  }
}

async function refreshSession(): Promise<boolean> {
  const locks = getOptionalLockManager(navigator);
  if (locks) {
    return locks.request('marina-admin-session-refresh', refreshOrReuseSession);
  }

  return refreshOrReuseSession();
}

function isSessionEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  return ['/auth/admin/login', '/auth/admin/refresh'].some((endpoint) =>
    url.includes(endpoint),
  );
}

httpClient.interceptors.response.use(undefined, async (error: unknown) => {
  if (!axios.isAxiosError(error)) throw error;

  const axiosError = error as AxiosError;
  const request = axiosError.config as RetryableRequest | undefined;
  if (
    axiosError.response?.status !== 401 ||
    !request ||
    request.sessionRefreshAttempted ||
    isSessionEndpoint(request.url)
  ) {
    throw error;
  }

  request.sessionRefreshAttempted = true;
  refreshRequest ??= refreshSession();
  const refreshed = await refreshRequest.finally(() => {
    refreshRequest = undefined;
  });

  if (!refreshed) throw error;
  return httpClient.request(request);
});
